import { useMemo, useState } from "react";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { X, Check } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAllRecipeIngredients, useCostSummary, useRecipeStages } from "@/hooks/recipes";
import { useOrgBranding } from "@/hooks/useOrgBranding";
import { RecipeMethodHeader } from "@/components/recipes/method/RecipeMethodHeader";
import { RecipeStagedSheet } from "@/components/recipes/method/RecipeStagedSheet";
import { RecipeFlowGrid } from "@/components/recipes/method/RecipeFlowGrid";
import { buildStagedMethod, isValidScale, scaledYield, type BatchScale } from "@/lib/recipes/staged-method";
import type { RecipeStep } from "@/components/recipes/execution-steps";
import type { Recipe } from "@/types/recipe";

/**
 * Compact is the flow grid (design 8b), each stage's steps a tap away;
 * extended is the staged sheet (8a), every step written out beside its
 * ingredients.
 */
type CookModeLayout = "compact" | "extended";

interface CookModeDialogProps {
  recipe: Recipe;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFinish?: () => Promise<void> | void;
  /**
   * The batch size to open at, for a cook mode that belongs to a cooking
   * session and so remembers it. Anything else opens at a full batch.
   */
  initialScale?: BatchScale;
  /** Called with the new batch size each time the cook changes it. */
  onScaleChange?: (scale: BatchScale) => void;
  /** The cooking session cook mode was opened from, recorded with a "Got it" on a critical control point. */
  cookingSessionId?: number;
}

/**
 * Cook mode: the recipe's method full screen, as the card views lay it out,
 * with the cook switching between the compact and the extended layout and
 * between a half and a full batch.
 */
export function CookModeDialog({ recipe, open, onOpenChange, onFinish, initialScale, onScaleChange, cookingSessionId }: CookModeDialogProps) {
  const { t } = useLingui();
  // Cook mode fills the app's main panel and leaves the sidebar, as the
  // recipe viewer designs (8a, 8b) frame it. Outside the app shell — tests,
  // or a page without one — it falls back to the whole window. Looked up while
  // open, because the panel is not in the DOM yet when a page first renders.
  const mainPanel =
    open && typeof document !== "undefined" ? document.querySelector<HTMLElement>("[data-main-panel]") : null;
  const branding = useOrgBranding();
  const [layout, setLayout] = useState<CookModeLayout>("extended");
  const startScale = initialScale !== undefined && isValidScale(initialScale) ? initialScale : 1;
  const [scale, setScale] = useState<BatchScale>(startScale);
  const [finishing, setFinishing] = useState(false);
  const stagesQuery = useRecipeStages(recipe.id, open);
  const linesQuery = useAllRecipeIngredients(recipe.id, open);
  // Each line's weight and volume, for the equivalents beside its amount. Extra
  // detail: the method draws without it and fills in when it lands.
  const { data: costSummary } = useCostSummary(recipe.id, open);
  const measures = useMemo(
    () => new Map(costSummary?.lines.map((line) => [line.ingredientId, line.measures ?? null])),
    [costSummary],
  );
  const stages = stagesQuery.data;
  const linesPage = linesQuery.data;

  const steps: RecipeStep[] = Array.isArray(recipe.instructions) ? (recipe.instructions as RecipeStep[]) : [];
  const yieldCount = scaledYield(recipe.yieldQuantity, scale);

  const changeScale = (next: BatchScale) => {
    setScale(next);
    onScaleChange?.(next);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setLayout("extended");
      setScale(startScale);
    }
    onOpenChange(next);
  };

  const handleFinish = async () => {
    setFinishing(true);
    try {
      await onFinish?.();
      handleOpenChange(false);
    } finally {
      setFinishing(false);
    }
  };

  return (
    // Not modal inside the main panel: the sidebar stays usable, and leaving
    // through it closes cook mode with the page it sits on.
    <Sheet open={open} onOpenChange={handleOpenChange} modal={!mainPanel}>
      <SheetContent
        side="bottom"
        hideClose
        container={mainPanel}
        // Closed by its own Close, Escape or Finish — not by focus or a click
        // landing outside it. The menu that opened it hands focus back to its
        // trigger, which would otherwise close it as it opens; and a sidebar
        // link leaves the page, which takes cook mode with it.
        onInteractOutside={mainPanel ? (event) => event.preventDefault() : undefined}
        className={cn(
          "top-0 flex flex-col gap-0 border-0 bg-(--color-surface-content) p-0",
          mainPanel ? "absolute h-full" : "h-dvh",
        )}
      >
        <SheetTitle className="sr-only">{recipe.name}</SheetTitle>
        <SheetDescription className="sr-only">{t`Cook mode`}</SheetDescription>

        {/* ── Top bar ── */}
        {/* The organization's mark lives here, as it did on the cook-mode
            panel: logo on the left, brand colour as the rule under the bar
            (T-1301). Unset renders a plain bar. */}
        <div
          className="flex shrink-0 items-center justify-between gap-4 border-b border-(--color-border) px-6 py-3"
          style={branding.primaryColor ? { borderBottomColor: branding.primaryColor, borderBottomWidth: 2 } : undefined}
        >
          <div className="flex min-w-0 items-center gap-4">
            {branding.logoUrl && <img src={branding.logoUrl} alt="" className="h-8 max-w-28 object-contain" />}
            <LayoutToggle value={layout} onChange={setLayout} />
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {onFinish && (
              <Button type="button" onClick={handleFinish} disabled={finishing} className="gap-1.5">
                <Check className="size-4" />
                {t`Finish`}
              </Button>
            )}
            <SheetClose className="text-(--color-text-secondary) transition-colors hover:text-(--color-text-primary)">
              <X className="size-6" />
              <span className="sr-only">{t`Close`}</span>
            </SheetClose>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto flex w-full max-w-[90rem] flex-col gap-5 px-6 py-6">
            <RecipeMethodHeader
              recipe={recipe}
              scale={scale}
              onScaleChange={changeScale}
              showPrint={false}
            />

            {stagesQuery.isError || linesQuery.isError ? (
              <div
                role="alert"
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-(--color-danger-bg) px-4 py-3 text-sm text-(--color-danger-text)"
              >
                <span>{t`This recipe's method couldn't be loaded.`}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (stagesQuery.isError) void stagesQuery.refetch();
                    if (linesQuery.isError) void linesQuery.refetch();
                  }}
                >
                  {t`Try again`}
                </Button>
              </div>
            ) : !stages || !linesPage ? (
              <Skeleton className="h-96 w-full" />
            ) : (
              <>
                {!stages.some((stage) => stage.kind === "STAGE") && (
                  <div className="rounded-xl bg-(--color-surface-muted) px-4 py-3 text-sm text-(--color-text-secondary)">
                    {t`This recipe's method isn't split into stages yet, so everything is shown as one group.`}
                  </div>
                )}
                {layout === "extended" ? (
                  <RecipeStagedSheet
                    method={buildStagedMethod(stages, linesPage.data, steps)}
                    scale={scale}
                    yieldQuantity={recipe.yieldQuantity}
                    measures={measures}
                    recipeId={recipe.id}
                    cookingSessionId={cookingSessionId}
                  />
                ) : (
                  <RecipeFlowGrid
                    method={buildStagedMethod(stages, linesPage.data, steps)}
                    scale={scale}
                    yieldCount={yieldCount}
                    measures={measures}
                    recipeId={recipe.id}
                    cookingSessionId={cookingSessionId}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function LayoutToggle({ value, onChange }: { value: CookModeLayout; onChange: (layout: CookModeLayout) => void }) {
  const { t } = useLingui();
  const options: { layout: CookModeLayout; label: string }[] = [
    { layout: "compact", label: t`Compact` },
    { layout: "extended", label: t`Extended` },
  ];

  return (
    <div role="group" aria-label={t`Recipe layout`} className="flex items-center gap-1 rounded-xl bg-(--color-surface-muted) p-1">
      {options.map((option) => (
        <button
          key={option.layout}
          type="button"
          aria-pressed={value === option.layout}
          onClick={() => onChange(option.layout)}
          className={cn(
            "rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors",
            value === option.layout
              ? "bg-(--color-surface-sidebar) text-(--color-text-on-dark)"
              : "text-(--color-text-secondary) hover:text-(--color-text-primary)",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
